/**
 * /admin/audit — read-only viewer for audit_log.
 *
 * Filters: actor / action / resourceType / time window. Cursor pagination.
 * Click a row → drawer with before/after JSON diff.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type AuditEntry, Routes } from '@tg-leads/contracts';

import { api } from '../../lib/api';

interface AuditList {
  items: AuditEntry[];
  nextCursor: string | null;
}

export function AuditLogPage() {
  const [filters, setFilters] = useState<{
    actor?: string;
    action?: string;
    resourceType?: string;
  }>({});
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const audit = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filters.actor) qs.set('actor', filters.actor);
      if (filters.action) qs.set('action', filters.action);
      if (filters.resourceType) qs.set('resourceType', filters.resourceType);
      qs.set('limit', '100');
      return api.get<AuditList>(`${Routes.admin.audit}?${qs}`);
    },
  });

  const setF = <K extends keyof typeof filters>(
    k: K,
    v: string,
  ) => setFilters((prev) => ({ ...prev, [k]: v || undefined }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">操作审计</h1>
        <p className="text-sm text-slate-500 mt-1">
          管理端和销售端所有写操作的留痕。点任一行查看变更前 / 变更后的字段对比。
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label">操作人（用户 ID 或 system）</label>
            <input
              className="input"
              value={filters.actor ?? ''}
              onChange={(e) => setF('actor', e.target.value)}
              placeholder="留空 = 全部"
            />
          </div>
          <div>
            <label className="label">操作动作（例如 lead.claim）</label>
            <input
              className="input"
              value={filters.action ?? ''}
              onChange={(e) => setF('action', e.target.value)}
            />
          </div>
          <div>
            <label className="label">资源类型</label>
            <input
              className="input"
              value={filters.resourceType ?? ''}
              onChange={(e) => setF('resourceType', e.target.value)}
              placeholder="account / lead / group / filter_rule / ..."
            />
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {audit.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : !audit.data?.items.length ? (
          <div className="text-slate-500 text-sm">没有匹配的记录</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-4">时间</th>
                <th className="py-2 pr-4">操作人</th>
                <th className="py-2 pr-4">动作</th>
                <th className="py-2 pr-4">资源</th>
                <th className="py-2 pr-4">请求 ID</th>
              </tr>
            </thead>
            <tbody>
              {audit.data.items.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="py-2 pr-4 font-mono">
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-mono">{shortenId(r.actor)}</td>
                  <td className="py-2 pr-4">{r.action}</td>
                  <td className="py-2 pr-4 font-mono">
                    {r.resourceType}/{shortenId(r.resourceId)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-slate-400">
                    {r.requestId ? shortenId(r.requestId) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <AuditDetailDrawer
          entry={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function AuditDetailDrawer({
  entry,
  onClose,
}: {
  entry: AuditEntry;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="absolute right-0 top-0 h-full w-[640px] bg-white shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <h2 className="text-lg font-semibold">{entry.action}</h2>
            <button
              className="text-slate-400 hover:text-slate-700 text-xl leading-none"
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <Row k="时间" v={new Date(entry.ts).toLocaleString()} />
          <Row k="操作人" v={entry.actor} />
          <Row k="资源" v={`${entry.resourceType} / ${entry.resourceId}`} />
          {entry.requestId && <Row k="请求 ID" v={entry.requestId} />}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mt-3 mb-1">
              变更前
            </h3>
            <JsonBlock value={entry.beforeJson} />
          </section>
          <section>
            <h3 className="text-sm font-semibold text-slate-700 mt-3 mb-1">
              变更后
            </h3>
            <JsonBlock value={entry.afterJson} />
          </section>
        </div>
      </aside>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex text-sm">
      <div className="w-24 text-slate-500">{k}</div>
      <div className="flex-1 font-mono break-all">{v}</div>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <div className="text-xs text-slate-400">—</div>;
  }
  return (
    <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 p-3 rounded">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function shortenId(id: string): string {
  if (id.length > 14) return id.slice(0, 8) + '…' + id.slice(-4);
  return id;
}
